import { Button } from '@/components/ui/button.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';

interface ExecuteBarProps {
  isExecuting: boolean;
  durationMs?: number;
  cacheHit?: boolean;
  rowCount?: number;
  onExecute: (fresh: boolean) => void;
  canExport?: boolean;
  onExport?: () => void;
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

export function ExecuteBar({
  isExecuting,
  durationMs,
  cacheHit,
  rowCount,
  onExecute,
  canExport = false,
  onExport,
}: ExecuteBarProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
        'bg-[var(--color-qup-bg-card-dark)] px-4 py-2.5',
      )}
    >
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => onExecute(false)}
          disabled={isExecuting}
          aria-busy={isExecuting ? 'true' : undefined}
        >
          {isExecuting ? (
            <span className="flex items-center gap-1.5">
              <svg
                className="h-3.5 w-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              {t('report.executing')}
            </span>
          ) : (
            t('report.execute')
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onExecute(true)}
          disabled={isExecuting}
          title={t('report.execute_fresh')}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0V5.36l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z"
              clipRule="evenodd"
            />
          </svg>
          <span className="sr-only">{t('report.execute_fresh')}</span>
        </Button>
      </div>

      {/* Stats */}
      {(durationMs !== undefined || cacheHit !== undefined || rowCount !== undefined) && (
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {rowCount !== undefined && (
            <span className="text-xs text-[var(--color-qup-text-muted-dark)]">
              {interpolate(t('report.rows_count'), { n: rowCount.toLocaleString('es') })}
            </span>
          )}
          {durationMs !== undefined && (
            <span className="text-xs text-[var(--color-qup-text-muted-dark)]">
              {interpolate(t('report.duration_ms'), { n: durationMs })}
            </span>
          )}
          {cacheHit === true && (
            <span className="inline-flex items-center rounded-full bg-[var(--color-qup-info)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-qup-info)]">
              {t('report.cache_hit')}
            </span>
          )}
          {canExport && onExport !== undefined && (
            <Button type="button" variant="outline" size="sm" onClick={onExport}>
              {t('report.export')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
